import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError, AxiosResponse } from 'axios';
import { getJobDefinition } from 'ducks/job-definition/operators';
import { JobDefinitionState } from 'ducks/job-definition/slices';
import { getJobDefinitionParameters } from 'ducks/job-definition-parameters/operators';
import { job_definitions_post_data, job_definitions_query } from 'ducks/job-definitions/constants';
import {
  getRunJobDefinitionParameters,
  getRunJobDefinitionPayload,
  getUpdatedDefinition,
} from 'ducks/job-definitions/utils';
import { createParameters, deleteParameters, updateParameters } from 'ducks/parameters/operators';
import { clearParameters } from 'ducks/parameters/slices';
import { getProject } from 'ducks/project/operators';
import { getProjectParameters } from 'ducks/project-parameters/operators';
import { getProjects } from 'ducks/projects/operators';
import { updateSettings } from 'ducks/settings/slices';
import { Slices } from 'ducks/slices';
import { ProjectsJobDefinitionsListAPI } from 'ducks/types/api-types';
import { JobDefinitionsCreatePayload } from 'ducks/types/payload-types';
import { Query } from 'ducks/types/query-types';
import { ProjectParameters, UpdateParameters } from 'ducks/types/store-types';
import { JobFormState } from 'modals/job-definition/constants';
import { store } from 'store';
import { Andromeda } from 'utils/axios';
import { handleError } from 'utils/errors';
import { replaceSearchQuery } from 'utils/queries';

export const getJobDefinitions = createAsyncThunk<ProjectsJobDefinitionsListAPI[], { project_id: number; data: Query }>(
  `${Slices.JOB_DEFINITIONS}/get`,
  async ({ project_id, data }, { rejectWithValue }) => {
    try {
      const post_data = {
        ...job_definitions_post_data,
        ...data,
      };
      const route = `/projects/${project_id}/job_definitions/list${replaceSearchQuery(post_data)}`;
      const res: AxiosResponse['data'] = await Andromeda.get(route);
      return res.data;
    } catch (error) {
      handleError(error);
      return rejectWithValue(error as AxiosError);
    }
  },
);

export const getJobDefinitionsPage = async (project_id: number, queries: Query) => {
  const query = {
    ...job_definitions_query,
    ...queries,
  };
  store.dispatch(updateSettings({ loading: true }));
  const res: AxiosResponse['data'] = await Promise.all([
    store.dispatch(getProjects()),
    store.dispatch(getProject(project_id)),
    store.dispatch(getProjectParameters(project_id)),
    store.dispatch(getJobDefinitions({ project_id, data: query })),
  ]);
  store.dispatch(updateSettings({ loading: false }));
  return res;
};

export const getJobDefinitionPage = async (project_id: number, job_definition_id: number) => {
  store.dispatch(updateSettings({ loading: true }));
  store.dispatch(clearParameters());

  const res: AxiosResponse['data'] = await Promise.all([
    store.dispatch(getProject(project_id)),
    store.dispatch(getProjects()),
    store.dispatch(getProjectParameters(project_id)),
    store.dispatch(getJobDefinition(job_definition_id)),
    store.dispatch(getJobDefinitionParameters(job_definition_id)),
  ]);
  store.dispatch(updateSettings({ loading: false }));
  return res;
};

export const archiveJobDefinition = async (entity_id: number) => {
  store.dispatch(updateSettings({ loading: true }));
  await Andromeda.post(`/job_definitions/${entity_id}/archive`, {});
};

export const unarchiveJobDefinition = async (entity_id: number) => {
  store.dispatch(updateSettings({ loading: true }));
  await Andromeda.post(`/job_definitions/${entity_id}/unarchive`, {});
};

export const createJobDefinition = async (project_id: number, data: JobDefinitionsCreatePayload) => {
  store.dispatch(updateSettings({ loading: true }));
  const res: AxiosResponse['data'] = await Andromeda.post(`/projects/${project_id}/job_definitions/create`, data);
  return res.data;
};

export const runJobDefinition = async (project_id: number, data: JobFormState, parameters: ProjectParameters) => {
  store.dispatch(updateSettings({ loading: true }));
  const { job_definition_id } = data;

  const payload = getRunJobDefinitionPayload(data);
  payload.parameters = getRunJobDefinitionParameters(parameters);

  // job definition but is not run command
  if (job_definition_id && job_definition_id === 1) {
    const res: AxiosResponse['data'] = await Andromeda.post(`/projects/${project_id}/jobs/create`, payload);
    return res.data;
  }

  // job definition and job definition is python
  if (job_definition_id && job_definition_id === 2) {
    throw new Error(
      'missing required parameters for running a python job definition. Run this job through the python SDK.',
    );
  }

  // job definition and job definition is fit model
  if (job_definition_id && job_definition_id === 3) {
    throw new Error(
      'missing required parameters for running a fit model job definition. Run this job through the python SDK.',
    );
  }

  // job definition and job definition is model
  if (job_definition_id && job_definition_id === 4) {
    throw new Error(
      'missing required parameters for running a model job definition. Run this job through the python SDK.',
    );
  }

  // job definition and job definition is model
  if (job_definition_id && job_definition_id === 5) {
    throw new Error(
      'missing required parameters for running a publish model job definition. Run this job through the python SDK.',
    );
  }

  // remaining job definitions
  if (job_definition_id) {
    const res: AxiosResponse['data'] = await Andromeda.post(`/job_definitions/${job_definition_id}/run`, payload);
    return res.data;
  }

  if (project_id) {
    const res: AxiosResponse['data'] = await Andromeda.post(`/projects/${project_id}/jobs/create`, payload);
    return res.data;
  }

  throw new Error('error capturing job definition id!');
};

export const updateJobDefinitionParameters = async (job_definition_id: number, data: UpdateParameters) => {
  const route = `/job_definitions/${job_definition_id}/parameters/by_name`;
  store.dispatch(updateSettings({ loading: true }));
  await updateParameters(route, data.update);
  await deleteParameters(route, data.delete);
  await createParameters(route, data.create);
};

export const updateJobDefinition = async (project_id: number, job_definition_id: number, data: JobDefinitionState) => {
  const definition = getUpdatedDefinition(data);
  store.dispatch(updateSettings({ loading: true }));
  const res: AxiosResponse['data'] = await Andromeda.post(`/job_definitions/${job_definition_id}/update`, definition);
  await getJobDefinitionPage(project_id, job_definition_id);
  return res.data;
};
